import { SITE, WAF_ERROR_MESSAGES } from '@/app/constants';
import { reportEvent } from '../sensor';
import { clearAwsWafCookie, getChallengeToken } from './aws-libs';

// 需要添加 AWS token 的接口路径
export const NEED_AWS_TOKEN_PATHS = [
  // 登录相关
  '/iam/v1/users/login',
  '/iam/v1/users/send-auth-code',
  '/iam/v1/users/captcha-gate',
  // Newsletter 订阅（未登录用户）
  '/iam/v1/users/subscribe-email',
  // 第三方登录
  '/iam/v1/users/login/third-party',
  // 创建模版任务
  '/vidu/v1/tasks/craft',
  // 创建任务
  '/vidu/v1/tasks',
  // // 获取任务状态 暂时先不添加
  // '/vidu/v1/tasks/state',
  // // 获取任务数量
  // '/vidu/v1/tasks/count',
  // 校验邀请码
  /^\/iam\/v1\/users\/validate-invite-code\/[A-Z0-9]+$/,
];

/**
 * 检查是否需要添加 AWS token
 * @param url 请求的 URL
 * @returns 是否需要添加 AWS token
 */
export const needAwsToken = (url: string): boolean => {
  const pathname = new URL(url).pathname;
  return NEED_AWS_TOKEN_PATHS.some((path) => {
    // 检查是否为正则表达式
    if (path instanceof RegExp) {
      return path.test(pathname);
    }
    // 否则使用完全匹配
    return pathname === path;
  });
};

/**
 * 为请求添加 AWS WAF token
 * @param headers 请求头
 * @param url 请求 URL
 * @returns 添加了 token 的请求头
 */
export const addAwsWafToken = async (headers: Headers, url: string): Promise<Headers> => {
  // 只有国外站且特定接口需要配置 AWS token
  if (SITE === 'global' && needAwsToken(url)) {
    try {
      // 获取验证码令牌
      const token = await getChallengeToken();
      headers.set('X-Aws-Waf-Token', token);
    } catch (error) {
      // 记录错误事件
      reportEvent('api_error', {
        api_error_message: error instanceof Error ? error.message : WAF_ERROR_MESSAGES.LOAD,
        api_error_name: error instanceof Error ? error.name : 'TypeError',
        api_url: url,
      });
    }
  }
  return headers;
};

/**
 * 处理 AWS WAF 响应
 * @param response 响应对象
 * @param url 请求 URL
 * @returns 处理后的响应，如果是 AWS WAF 错误，则抛出异常
 */
export const handleAwsWafResponse = (response: Response, url: string): Response => {
  if (SITE !== 'global') {
    return response;
  }

  // 此处处理的是接口请求返回的信息
  const reportInfo = {
    api_error_reason: '',
    api_status_code: response.status,
    api_url: url,
  };

  switch (response.status) {
    case 405:
      reportInfo.api_error_reason = WAF_ERROR_MESSAGES.CAPTCHA;
      break;
    case 403:
      if (response.headers.get('Content-Length') === '0') {
        reportInfo.api_error_reason = WAF_ERROR_MESSAGES.FORBIDDEN;
      }
      break;
    case 202:
      reportInfo.api_error_reason = WAF_ERROR_MESSAGES.CHALLENGE;
      break;
  }

  if (reportInfo.api_error_reason) {
    reportEvent('api_error', reportInfo);
  }

  // 202 是 aws waf 返回的，不是正常的接口返回，
  // 但是 fetch 会把这个当成正常的返回，所以这里需要抛出异常
  if (response.status === 202) {
    throw new Error(WAF_ERROR_MESSAGES.CHALLENGE);
  }
  if (response.status === 405) {
    throw new Error(WAF_ERROR_MESSAGES.CAPTCHA);
  }

  return response;
};

/**
 * 处理 AWS WAF 错误
 * @param error 错误对象
 * @param refresh 重试函数
 * @param showChallengeError 显示202 Challenge错误函数
 * @returns 是否已完全处理
 */
export const handleAwsWafError = async (
  error: Error,
  refresh?: () => void,
  showChallengeError?: () => void,
): Promise<boolean> => {
  // 验证码错误
  if (isAwsWafCaptchaError(error)) {
    try {
      await window.showAWSCaptcha?.();
      refresh?.();
      return true;
    } catch {
      return false;
    }
  }

  // 202 Challenge 错误，清除 cookie
  if (isAwsWafChallengeError(error)) {
    clearAwsWafCookie();
    showChallengeError?.();
    return true;
  }

  if (!isAwsWafError(error)) {
    // 上报非 AWS WAF 错误
    reportEvent('api_error', {
      api_error_message: error.message,
      api_error_name: error.name,
    });
  }

  return false;
};

/**
 * 判断错误是否为 AWS WAF 错误
 * @param error 错误对象
 * @returns 是否为 AWS WAF 错误
 */
export const isAwsWafError = (error: Error): boolean => {
  return SITE === 'global' && error.message.startsWith('AWS WAF');
};

// waf Captcha 错误
export const isAwsWafCaptchaError = (error: Error): boolean => {
  return SITE === 'global' && error.message === WAF_ERROR_MESSAGES.CAPTCHA;
};

// waf 202 Challenge Error
export const isAwsWafChallengeError = (error: Error): boolean => {
  return SITE === 'global' && error.message === WAF_ERROR_MESSAGES.CHALLENGE;
};
